import React from 'react';
import * as _ from 'lodash';
import { connect } from 'react-redux';
import { sessionService } from 'redux-react-session';
import clientService, { reconcileUser } from '../../services/client';
import { logoutUser } from '../../actions/client';
import { getCookie, getAccountsDashboardUrl } from '../../services/utilityService';
import LoadingScreen from '../LoadingScreen';

// EN-583: how long a cached session is trusted before we revalidate it against
// /auth/clients in the background (stale-while-revalidate). 10 minutes.
const SESSION_REVALIDATE_TTL_MS = 10 * 60 * 1000;

export function withUser(WrappedComponent) {
    const mapStateToProps = ({ session }) => ({
        sessionUser: session.user
    });
    return connect(mapStateToProps)(
        class extends React.Component {
            constructor(props) {
                super(props);
                this.state = {
                    isAuth: false,
                    user: null,
                    error: null
                };
            }

            componentDidMount() {
                this._isMounted = true;
                const cookie = getCookie();

                if (cookie) {
                    sessionService
                        .loadUser()
                        .then((user) => {
                            if (user.credentials.accessToken === cookie) {
                                // Cache hit: paint immediately for instant UX,
                                // then revalidate in the background (EN-583).
                                this.setUser(user);
                                this.maybeRevalidate(user);
                            } else {
                                this.authUser();
                            }
                        })
                        .catch((e) => {
                            this.authUser();
                        });
                } else {
                    this.logout();
                }
            }

            componentWillUnmount() {
                this._isMounted = false;
            }

            // EN-583: stale-while-revalidate. The cached user has already been
            // painted; here we silently refresh it from /auth/clients if the
            // cached copy is older than the TTL. The permanent monolith
            // access_token never rotates, so without this a stale session
            // (e.g. one built before admin_email_address existed) is served
            // forever and the Requests form drops c_submitter_email.
            maybeRevalidate = (cachedUser) => {
                if (
                    cachedUser._lastValidatedAt &&
                    Date.now() - cachedUser._lastValidatedAt < SESSION_REVALIDATE_TTL_MS
                ) {
                    // Fast path: validated recently, skip the network call.
                    return;
                }

                clientService
                    .auth()
                    .then((fresh) => {
                        // Reconcile against the CURRENT session, not the
                        // mount-time snapshot. If a multi-account admin switched
                        // company while this revalidate was in flight, that
                        // selection now lives in the session; reconciling against
                        // the stale snapshot would silently revert it. Mirrors
                        // AccountSelectionComponent.switchClients' fresh load.
                        return sessionService
                            .loadUser()
                            .then((current) => {
                                const merged = reconcileUser(current, fresh);
                                merged._lastValidatedAt = Date.now();
                                return sessionService.saveUser(merged).then(() => {
                                    if (this._isMounted) {
                                        this.setUser(merged);
                                    }
                                });
                            })
                            .catch(() => {
                                // Couldn't re-read the session: keep the
                                // already-painted state. No crash, no logout.
                            });
                    })
                    .catch((e) => {
                        if (e?.response?.status === 401) {
                            // Session truly invalid -> send them to log in
                            // (mirrors the 401 handling in render()).
                            if (this._isMounted) {
                                this.goToUrl(
                                    `${getAccountsDashboardUrl()}/login?r=${encodeURI(window.location.href)}`
                                );
                            }
                            return;
                        }
                        // Transient (network/5xx) failure: keep the cached
                        // session. A revalidation hiccup must never break a
                        // working session.
                        console.error('Session revalidation failed; keeping cached session', e);
                    });
            };

            setUser = (user) => {
                this.setState({
                    user,
                    isAuth: true
                });
            };

            setError = (error) => {
                this.setState({
                    isAuth: false,
                    error
                });
            };

            logout = () => {
                this.props.dispatch(logoutUser(window.location));
            };

            authUser = () => {
                clientService
                    .auth()
                    .then((user) => {
                        sessionService.saveSession(user.credentials.accessToken).then(() => {
                            sessionService.saveUser(user).then(() => {
                                this.setUser(user);
                            });
                        });
                    })
                    .catch((e) => {
                        console.log(e);
                        this.setError({
                            code: e?.response?.status,
                            message: e?.response?.data?.message
                        });
                    });
            };

            goToUrl = (url) => {
                window.location.href = url;
            };

            handleSetUser = (user) => {
                this.setUser(user);
            };

            componentDidUpdate() {
                if (
                    this.state.isAuth &&
                    !_.isEmpty(this.props.sessionUser) &&
                    JSON.stringify(this.props.sessionUser) !== JSON.stringify(this.state.user)
                ) {
                    this.setUser(this.props.sessionUser);
                }
            }

            render() {
                if (this.state.error) {
                    if (this.state.error.code === 401) {
                        //unauthorized, ask them to login
                        this.goToUrl(
                            `${getAccountsDashboardUrl()}/login?r=${encodeURI(window.location.href)}`
                        );
                        return <LoadingScreen />;
                    } else if (this.state.error.code === 403) {
                        this.goToUrl(`${getAccountsDashboardUrl()}`);
                        return <LoadingScreen />;
                    } else {
                        this.goToUrl(`${getAccountsDashboardUrl()}`);
                        return <LoadingScreen />;
                    }
                } else {
                    return this.state.isAuth ? (
                        <WrappedComponent
                            user={this.state.user}
                            setUser={this.handleSetUser}
                            {...this.props}
                        />
                    ) : (
                        <LoadingScreen />
                    );
                }
            }
        }
    );
}
